import type {
  ResultInnerDetail,
  ResultQueryRanking,
  ResultScoreDetails,
  ResultSort,
} from '@empathybroker/tsclient-play/dist/models/search/result'

import type { Result } from '@empathyco/x-types'
import type { QueryRankingElementActionConfig } from '../composables/use-query-ranking-element-actions-config'
import type {
  Dictionary,
  ExplainMessages,
  ExplainScoreCriteriaMessages,
  ExplainScoreMessages,
  ExplainTableData,
} from '../models'

import type { EqualizeFieldNamesProvider } from '../providers/interfaces'
// @ts-strict-ignore
import { ResultSortCriteriaIdEnum } from '@empathybroker/tsclient-play/dist/enums/search/debug/result/result-sort-criteria-id.enum'

import {
  QueryRankingCategoryTypeEnum,
  RootScoreCriteriaTypeEnum,
} from '@empathybroker/tsclient-play/dist/enums/search/debug/result/score-criteria-detail-type.enum'
import {
  HiddenElementsAction,
  PinProductAction,
  QueryRankingElementRankingAction,
} from '@empathyco/playboard-core-domain'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { ColorEnum } from '../enums'
import { ConfigurationsUtils } from './configurations.utils'
import { NumberUtils } from './number.utils'

interface IProduct {
  id: string
  details: Array<IProductDetail>
  parent?: string
}

interface IProductDetail {
  fieldName: string
  fieldValue?: string
  value?: number
  type?: string
  details?: Array<IProductDetail>
}

interface IRow {
  fieldName: string
  parent: string
  values?: Array<string>
  details?: Array<IRow>
}

const EMPTY_VALUE = '-'

/**
 * Summary. Explain utils class. The aim of this utils is to give support for the interface giving
 * useful methods to print debug data
 */
export class ExplainUtils {
  /**
   * Summary. get Result score criteria
   *
   * Description. Get the criteria data when the sortCriteria Id is SCORE
   *
   * @param {result} result
   *
   * @return {ResultSortScoreCriteria}
   */
  static getResultScoreCriteria(result: Result): ResultSort {
    const resultScoreCriteria = result.debug
      ? result.debug.sort.find(
          (sortCriteria: ResultSort) => sortCriteria.id === ResultSortCriteriaIdEnum.SCORE,
        )
      : null
    const defaultResultScoreCriteria = {
      id: ResultSortCriteriaIdEnum.SCORE,
      value: 0,
      details: { value: 0 },
    }
    return resultScoreCriteria || defaultResultScoreCriteria
  }

  /**
   * Summary. Find Score Criteria Detail
   *
   * Description. Find any criteria detail given its type by performing a deep search inside a
   * dictionary
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   * @param {Dictionary} details
   *
   * @return {ScoreCriteriaDetail | Dictionary}
   */
  private static findScoreCriteriaDetail(
    criteriaDetailType: RootScoreCriteriaTypeEnum | string,
    details: Dictionary,
  ): ResultSort | Dictionary {
    let detailFound: ResultScoreDetails | Dictionary
    if (Array.isArray(details)) {
      detailFound = details.find(
        (detail: ResultInnerDetail) => criteriaDetailType === detail.fieldName,
      ) as ResultInnerDetail
    } else {
      for (const [currentDetailType, currentDetail] of Object.entries(details)) {
        if (currentDetailType === criteriaDetailType) {
          detailFound = currentDetail
        } else if (currentDetail.details) {
          const innerCurrentDetail = this.findScoreCriteriaDetail(
            criteriaDetailType,
            currentDetail.details as Dictionary,
          )
          if (innerCurrentDetail) {
            detailFound = innerCurrentDetail
          }
        }
      }
    }
    return detailFound
  }

  /**
   * Summary. Result ScoreCriteriaDetail icon
   *
   * Description. This will return the associated icon ligature for each ScoreCriteriaDetail
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   *
   * @return {string} icon ligature / undefined if criteriaDetailType is not known.
   */
  static getResultScoreCriteriaDetailIcon(
    criteriaDetailType: RootScoreCriteriaTypeEnum | string,
  ): string {
    const iconOptions = {
      [RootScoreCriteriaTypeEnum.EQUALIZE]: 'equalize',
      [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: 'explain',
      [RootScoreCriteriaTypeEnum.QUERY_RANKING]: 'boostbury',
      [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: 'usergroup',
      [RootScoreCriteriaTypeEnum.PERSONALIZATION]: 'personalize',
      [RootScoreCriteriaTypeEnum.TERM_POSITION]: 'hash',
    }
    return iconOptions[criteriaDetailType]
  }

  /**
   * Summary. Result ScoreCriteriaDetail color
   *
   * Description. This will return the associated color for each ScoreCriteriaDetail
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   *
   * @return {ColorEnum} ColorEnum / undefined if criteriaDetailType is not known.
   */
  static getResultScoreCriteriaDetailColor(
    criteriaDetailType: RootScoreCriteriaTypeEnum | string,
  ): ColorEnum {
    const colorOptions = {
      [RootScoreCriteriaTypeEnum.EQUALIZE]: ColorEnum.BLUE_50,
      [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: ColorEnum.NEUTRAL_95,
      [RootScoreCriteriaTypeEnum.QUERY_RANKING]: ColorEnum.GREEN_75,
      [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: ColorEnum.PURPLE_50,
      [RootScoreCriteriaTypeEnum.PERSONALIZATION]: ColorEnum.YELLOW_50,
      [RootScoreCriteriaTypeEnum.TERM_POSITION]: ColorEnum.ORANGE_50,
    }
    return colorOptions[criteriaDetailType]
  }

  /**
   * Summary. Result ScoreCriteriaDetail background color
   *
   * Description. This will return the associated color for each ScoreCriteriaDetail
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   *
   * @return {ColorEnum} ColorEnum / undefined if criteriaDetailType is not known.
   */
  static getResultScoreCriteriaDetailBackgroundColor(
    criteriaDetailType: RootScoreCriteriaTypeEnum | string,
  ): ColorEnum {
    const colorOptions = {
      [RootScoreCriteriaTypeEnum.EQUALIZE]: ColorEnum.BLUE_5,
      [RootScoreCriteriaTypeEnum.SEMANTIC_SEARCH]: ColorEnum.NEUTRAL_10,
      [RootScoreCriteriaTypeEnum.QUERY_RANKING]: ColorEnum.GREEN_25,
      [RootScoreCriteriaTypeEnum.WISDOM_OF_THE_CROWD]: ColorEnum.PURPLE_25,
      [RootScoreCriteriaTypeEnum.PERSONALIZATION]: ColorEnum.YELLOW_25,
      [RootScoreCriteriaTypeEnum.TERM_POSITION]: ColorEnum.ORANGE_25,
    }
    return colorOptions[criteriaDetailType]
  }

  /**
   * Summary. Result ScoreCriteriaDetail messages
   *
   * Description. This will return the necessary messages of each ScoreCriteriaDetail needed in the
   * interface.
   *
   * @param {ScoreCriteriaDetailTypeEnum | string} criteriaDetailType
   * @param {ExplainScoreMessages} messages
   *
   * @return {ExplainScoreCriteriaMessages}
   */
  static getResultScoreCriteriaDetailMessages(
    criteriaDetailType: RootScoreCriteriaTypeEnum | string,
    messages: ExplainScoreMessages,
  ): ExplainScoreCriteriaMessages {
    const criteriaDetailMessages = messages.criterias[criteriaDetailType]
    return criteriaDetailMessages || { label: criteriaDetailType }
  }

  static translateResultsToRowData(
    results: Array<Result>,
    messages: ExplainMessages['tableRows'],
  ): Array<ExplainTableData> {
    const detailsObjToArray = (
      details: ResultScoreDetails | Record<string, IProductDetail> | Array<IProductDetail>,
    ): Array<IProductDetail> => {
      if (Array.isArray(details)) {
        return details
      }
      delete details.value

      return Object.keys(details).map(fieldName => ({
        fieldName,
        value: details[fieldName].value,
        details: detailsObjToArray(details[fieldName].details),
      }))
    }

    const getSectionDetails = (
      section: string,
      fields: Array<string>,
      parentField: string,
      details: Array<IProductDetail>,
    ): Array<IProductDetail> => {
      details.forEach(detail => {
        const parent = detail.details.find(fieldDetail => fieldDetail.fieldName === parentField)
        if (!parent?.details) {
          return
        }
        const sectionDetails = parent.details.filter(field => fields.includes(field.fieldName))
        parent.details = parent.details.filter(field => !fields.includes(field.fieldName))
        if (sectionDetails.length) {
          detail.details.splice(detail.details.indexOf(parent) + 1, 0, {
            fieldName: section,
            value: undefined,
            details: sectionDetails,
          })
        }
      })
      return details
    }

    const provider = CONTAINER.get<EqualizeFieldNamesProvider>(TYPES.EqualizeFieldNamesProvider)()
    const createPinnedProductDetails = (product: Result): Array<IProductDetail> => [
      {
        fieldName: RootScoreCriteriaTypeEnum.QUERY_RANKING,
        details: [
          {
            fieldName: QueryRankingCategoryTypeEnum.PRODUCTS,
            details: [
              {
                fieldName: product.id.toString(),
                fieldValue: product.id.toString(),
                type: PinProductAction,
              },
            ],
          },
        ],
      },
    ]
    const getDetailsOfProducts = (res: Array<Result>): Array<IProduct> =>
      res.map(product => ({
        id: product.id.toString(),
        details: ((): Array<IProductDetail> => {
          const score = this.getResultScoreCriteria(product)

          const details = this.isResultPinned(product)
            ? createPinnedProductDetails(product)
            : detailsObjToArray(score.details)

          const semanticMatchDetails = getSectionDetails(
            'semanticMatch',
            provider.semanticFieldNames,
            'textualMatch',
            details,
          )
          const sponsoredScoreDetails = getSectionDetails(
            'sponsoredScore',
            provider.sponsoredFieldNames,
            'functionScore',
            details,
          )
          return [...details, ...semanticMatchDetails, ...sponsoredScoreDetails]
        })(),
      }))

    const products = getDetailsOfProducts(results)

    const buildRowStructure = (
      productId: IProduct['id'],
      productDetails: Array<IProductDetail>,
      rows: Array<IRow>,
      parent: string,
    ): Array<IRow> => {
      productDetails.forEach(detail => {
        const fieldName =
          parent === QueryRankingCategoryTypeEnum.PRODUCTS ? detail.type : detail.fieldName

        const rowIndexOfCorrespondingProductDetail = rows.findIndex(
          row => row.fieldName === fieldName,
        )
        const haveToAddNewRow = rowIndexOfCorrespondingProductDetail === -1
        const children = detail.details
        if (haveToAddNewRow) {
          rows.push({
            ...{ fieldName, parent },
            ...(children
              ? { details: buildRowStructure(productId, children, [], detail.fieldName) }
              : { ids: [], values: [] }),
          })
        } else {
          rows[rowIndexOfCorrespondingProductDetail] = {
            ...rows[rowIndexOfCorrespondingProductDetail],
            ...(children
              ? {
                  details: buildRowStructure(
                    productId,
                    children,
                    rows[rowIndexOfCorrespondingProductDetail].details,
                    detail.fieldName,
                  ),
                }
              : {}),
          }
        }
      })
      return rows
    }
    const productToRowsReducer = (rows, product: IProduct) =>
      product.details ? buildRowStructure(product.id, product.details, rows, null) : rows

    const getProductNodeValue = (
      rowNodePath: string,
      productNodePath: string,
      productNode: IProductDetail,
    ): Partial<IProductDetail> => {
      const fieldName =
        productNodePath === '.queryRanking.products' ? productNode.type : productNode.fieldName
      const prodNodePath = productNode.fieldName
        ? `${productNodePath}.${fieldName}`
        : productNodePath
      const children: Array<IProductDetail> = productNode.details
      if (prodNodePath === rowNodePath) {
        return { value: productNode.value, type: productNode.type || undefined }
      } else if (children) {
        return children.reduce((val, node) => {
          return val || getProductNodeValue(rowNodePath, prodNodePath, node)
        }, null)
      } else {
        return null
      }
    }

    const getProductsValuesIds = (
      rowNodePath: string,
      prods: Array<IProduct>,
    ): Array<{ id: IProduct['id']; value: string | number; type: string }> =>
      prods.map(product => {
        const id = product.id
        const nodeValue = getProductNodeValue(rowNodePath, '', {
          fieldName: undefined,
          value: undefined,
          details: product.details,
        })
        let value: string | number
        if (nodeValue) {
          value = NumberUtils.truncNumberWithoutRounding(nodeValue.value)
        } else {
          value = EMPTY_VALUE
        }
        return { id, value, type: nodeValue?.type }
      })

    const flattenRowDetails = (
      row: ExplainTableData,
      rowsToFlatten?: [{ parent: string; fieldName: string; label: string }],
    ): ExplainTableData => {
      const rowToFlatten = rowsToFlatten?.find(
        ({ parent, fieldName }) => row.parent === parent && row.fieldName === fieldName,
      )

      if (rowToFlatten) {
        const rowDetail: ExplainTableData = {
          fieldName: rowToFlatten.label,
          parent: rowToFlatten.fieldName,
          ids: row.ids,
          values: [],
          types: [],
        }

        row.details.forEach(detail => {
          detail.values.forEach((value, index) => {
            if (value !== EMPTY_VALUE) {
              rowDetail.values[index] = value
            } else {
              rowDetail.values.push(value)
            }
          })

          detail.types.forEach((value, index) => {
            if (value) {
              rowDetail.types[index] = value
            } else {
              rowDetail.types.push(value)
            }
          })
        })

        return {
          ...row,
          details: [rowDetail],
        }
      }

      return row
    }
    const addProductDataToRows = (
      rows: Array<ExplainTableData>,
      prods: Array<IProduct>,
      nodePath: string,
    ): Array<ExplainTableData> => {
      return rows.map(row => {
        const children = row.details
        if (children) {
          row.details = addProductDataToRows(children, prods, `${nodePath}.${row.fieldName}`)
        } else {
          const valuesAndIds = getProductsValuesIds(`${nodePath}.${row.fieldName}`, prods)
          row.values = valuesAndIds.map(res => res.value)
          row.types = valuesAndIds.map(res => res.type)
        }
        row.ids = prods.map(p => p.id)

        return flattenRowDetails(row, [
          {
            parent: RootScoreCriteriaTypeEnum.QUERY_RANKING,
            fieldName: QueryRankingCategoryTypeEnum.PRODUCTS,
            label: messages.queryRanking.product,
          },
        ])
      })
    }

    const tableRowStructure = products.reduce<Array<ExplainTableData>>(productToRowsReducer, [])
    return addProductDataToRows(tableRowStructure, products, '')
  }

  static getQueryRankingDetailsFromResultScoreCriteria(
    result: Result,
  ): ResultQueryRanking['details'] | undefined {
    const score = this.getResultScoreCriteria(result)
    return score?.details?.queryRanking?.details
  }

  static getQueryRankingConfigActionFromResult(
    result: Result,
    hiddenProducts: boolean = false,
  ): QueryRankingElementActionConfig['action'] | undefined {
    if (hiddenProducts) {
      return HiddenElementsAction
    }

    if (result.debug.pin?.position) {
      return PinProductAction
    }

    const queryRanking = this.getQueryRankingDetailsFromResultScoreCriteria(result)
    if (queryRanking?.products && Array.isArray(queryRanking.products?.details)) {
      const productDetail = ConfigurationsUtils.findQueryRankingConfigByFieldValue(
        result.id.toString(),
        queryRanking.products.details,
      )
      if (productDetail) {
        return this.getQueryRankingElementConfigAction(productDetail.type)
      }
    }

    return undefined
  }

  /**
   * Summary. Get attribute results ranking action from result
   *
   * Description. Checks if there are any attribute boosts or buries in the queryRanking.attributes.details
   * and returns the corresponding action type of the first attribute found.
   *
   * @param {Result} result - The result to check for attribute boosts/buries
   *
   * @return {QueryRankingElementRankingAction | undefined} - The type of attribute boost/bury found (BOOST | BURY) or undefined if none found
   */
  static getAttributeQueryRankingActionFromResult(
    result: Result,
  ): QueryRankingElementRankingAction | undefined {
    const queryRanking = this.getQueryRankingDetailsFromResultScoreCriteria(result)

    if (
      queryRanking?.attributes &&
      Array.isArray(queryRanking.attributes?.details) &&
      queryRanking.attributes.details.length > 0
    ) {
      return this.getQueryRankingElementConfigAction(queryRanking.attributes.details[0].type)
    }

    return undefined
  }

  static getQueryRankingElementConfigAction(
    type: ResultInnerDetail['type'],
  ): QueryRankingElementRankingAction | undefined {
    if (type === QueryRankingElementRankingAction.BOOST.toLowerCase()) {
      return QueryRankingElementRankingAction.BOOST
    }

    if (type === QueryRankingElementRankingAction.BURY.toLowerCase()) {
      return QueryRankingElementRankingAction.BURY
    }

    return undefined
  }

  static getScoreCriteriaValue(result: Result): number | typeof EMPTY_VALUE {
    const score = this.getResultScoreCriteria(result)
    if (this.isResultPinned(result) || !score.value) {
      return EMPTY_VALUE
    }

    return score.value
  }

  static isResultPinned(result: Result): boolean {
    return !!result.debug?.pin?.position
  }

  static isResultBuried(result: Result): boolean {
    const queryRankingScoreDetail = this.findScoreCriteriaDetail(
      RootScoreCriteriaTypeEnum.QUERY_RANKING,
      this.getResultScoreCriteria(result).details,
    )

    return !![...(queryRankingScoreDetail?.details?.products?.details ?? [])].find(
      (resultInnerDetail: ResultInnerDetail) => resultInnerDetail.type === 'bury',
    )
  }
}
